import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import cn from 'classnames'
import { openWindow } from 'models/window/actions'
import { modalNames } from 'features/modal/constants/names'
import { apiClient } from 'core/http'
import { useAuthContext } from 'core/providers/AuthProvider'
import { toast } from 'react-toastify'
import { formatNumbers } from 'core/helpers/format-numbers'
import friendsIcon from '../../banner/assets/images/friends-icon.png'
import giftBoxSmall from './assets/images/gift-box-small.png'
import coinIcon from 'features/leaderboard/tabs/assets/images/coin.svg'
import lockIcon from './assets/images/lock.svg'
import hexGreenSvg from './assets/images/hex-green.svg'
import checkIcon from './assets/images/check-icon.svg'
import c from './assets/sass/item.module.scss'

const pluralFriends = (n) => {
    const mod10 = n % 10
    const mod100 = n % 100
    if (mod10 === 1 && mod100 !== 11) return 'Друг'
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'Друга'
    return 'Друзей'
}

const HexGold = ({ children }) => (
    <div className={c.hexWrap}>
        <svg className={c.hexSvg} viewBox="0 0 33 36" fill="none">
            <path d="M13.6349 0.666C15.1765-0.222 17.0659-0.222 18.6075 0.666L29.6995 7.06C31.2715 7.966 32.2424 9.658 32.2424 11.491V24.146C32.2424 25.979 31.2715 27.67 29.6995 28.576L18.6075 34.97C17.0659 35.858 15.1765 35.858 13.6349 34.97L2.543 28.576C0.971 27.67 0 25.979 0 24.146V11.491C0 9.658 0.971 7.966 2.543 7.06L13.6349 0.666Z" fill="url(#hexGold)" />
            <defs>
                <linearGradient id="hexGold" x1="16.17" y1="35.64" x2="16.17" y2="-3.82" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#EBA626" />
                    <stop offset="1" stopColor="#FFD359" />
                </linearGradient>
            </defs>
        </svg>
        <div className={c.hexContent}>{children}</div>
    </div>
)

const HexDark = ({ children }) => (
    <div className={c.hexWrap}>
        <svg className={c.hexSvg} viewBox="0 0 33 36" fill="none">
            <path d="M13.6349 0.666C15.1765-0.222 17.0659-0.222 18.6075 0.666L29.6995 7.06C31.2715 7.966 32.2424 9.658 32.2424 11.491V24.146C32.2424 25.979 31.2715 27.67 29.6995 28.576L18.6075 34.97C17.0659 35.858 15.1765 35.858 13.6349 34.97L2.543 28.576C0.971 27.67 0 25.979 0 24.146V11.491C0 9.658 0.971 7.966 2.543 7.06L13.6349 0.666Z" fill="#6F1919" />
        </svg>
        <div className={c.hexContent}>{children}</div>
    </div>
)

const HexGreen = ({ children }) => (
    <div className={c.hexWrap}>
        <img className={c.hexSvg} src={hexGreenSvg} alt="" />
        <div className={c.hexContent}>{children}</div>
    </div>
)

export const MilestoneItem = ({ threshold, rewardType = 'case', coinAmount = 0, caseImage, caseName, isClaimed, isAvailable, isLocked, isFirst, isLast, onClaimed }) => {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const { updateUserData } = useAuthContext()
    const [loading, setLoading] = useState(false)

    const isCoins = rewardType === 'coins'

    const handleClaim = async () => {
        if (isCoins) {
            if (loading) return
            setLoading(true)
            try {
                const res = await apiClient.post(`/friends/milestones/${threshold}/claim`, { game: 'coins' })
                if (res?.data?.success) {
                    toast(`+${formatNumbers(coinAmount)} ${t('tasks.item.coins')}`, { type: 'success' })
                    await updateUserData()
                    if (onClaimed) onClaimed()
                }
            } catch (e) {
                const msg = e?.response?.data?.error || t('common.error')
                toast(msg, { type: 'error' })
            } finally {
                setLoading(false)
            }
            return
        }
        dispatch(openWindow(modalNames.GAME_SELECT_MODAL, { threshold, onClaimed }))
    }

    return (
        <div className={cn(c.component, {
            [c.claimed]: isClaimed,
            [c.available]: isAvailable,
            [c.locked]: isLocked,
        })}>
            <div className={c.left}>
                <div className={c.leftCard}>
                    <img className={c.friendsIcon} src={friendsIcon} alt="" />
                    <div className={c.friendsInfo}>
                        <span className={c.friendsNumber}>{threshold}</span>
                        <span className={c.friendsLabel}>{pluralFriends(threshold)}</span>
                    </div>
                </div>
            </div>

            <div className={c.center}>
                <div className={c.markerWrap}>
                    {isAvailable && (
                        <HexGold>
                            <img className={c.markerIcon} src={giftBoxSmall} alt="" />
                        </HexGold>
                    )}
                    {isLocked && (
                        <HexDark>
                            <img className={c.markerIcon} src={giftBoxSmall} alt="" />
                        </HexDark>
                    )}
                    {isClaimed && (
                        <HexGreen>
                            <img className={c.markerCheck} src={checkIcon} alt="" />
                        </HexGreen>
                    )}
                </div>
            </div>

            <div className={c.right}>
                <div className={cn(c.rewardCard, {
                    [c.rewardAvailable]: isAvailable,
                    [c.rewardClaimed]: isClaimed,
                    [c.rewardLocked]: isLocked,
                    [c.rewardCase]: !isCoins && (isAvailable || isClaimed),
                })}>
                    {!isCoins && (isAvailable || isClaimed) && (
                        <span className={c.rewardBanner}>{t('profile.cases.case', 'Кейс')}</span>
                    )}
                    {(isAvailable || isClaimed) && (
                        <>
                            {isCoins ? (
                                <>
                                    <span className={c.rewardLabel}>{formatNumbers(coinAmount)}</span>
                                    <img className={c.rewardImageCoins} src={coinIcon} alt="" />
                                </>
                            ) : (
                                <img
                                    className={c.rewardImage}
                                    src={caseImage || giftBoxSmall}
                                    alt=""
                                />
                            )}
                        </>
                    )}
                    {isAvailable && (
                        <button className={c.claimBtn} onClick={handleClaim} disabled={loading}>
                            {loading ? '...' : t('earnings.milestones.get')}
                        </button>
                    )}
                    {isClaimed && (
                        <span className={c.claimedText}>{t('earnings.milestones.claimed')}</span>
                    )}
                    {isLocked && (
                        <div className={c.lockedInfo}>
                            <img className={c.lockIcon} src={lockIcon} alt="" />
                            <span className={c.lockedTitle}>{t('earnings.milestones.locked')}</span>
                            <span className={c.lockedHint}>{t('earnings.milestones.lockedHint')}</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
